import {
  FC,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { QueryResults } from '@/components/DataWorkspace/QueryResults'
import { StandardLoader } from '@/components/StandardLoader'
import { SqlTabToolbar } from './SqlTabToolbar'
import { GenerateDataChart } from '@/components/DataWorkspace/GenerateDataChart'
import { QueryResult } from '@/components/DataWorkspace/types'
import { useQueryRunner } from '@/components/DataWorkspace/useQueryRunner'
import { useAtomValue } from 'jotai'
import { useTar } from '@/modules/i18n'
import { useEventEmitter } from '@/modules/EventBusModule'
import {
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@duck/ui'
import { getTableDataFromQuery } from '@/components/DataWorkspace/utils'
import { cn } from '@duck/ui'
import {
  workspaceChallengeIdAtom,
  workspaceTaskAtom,
  workspaceTabsHasTwoViewsAtom,
} from '@/store/workspaceAtoms'
import { toast } from 'sonner'

const noop = (): void => undefined

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const CodeInput = lazy(() =>
  import('@/components/DataWorkspace/CodeInput').then((module) => ({
    default: module.CodeInput,
  }))
)

interface SqlTabProps {
  query?: string
  onUpdateData?: (query: string) => void
  isVisible?: boolean
}

export const SqlTab: FC<SqlTabProps> = ({ query: queryProp, onUpdateData, isVisible = true }) => {
  const { t } = useTar()
  const challengeId = useAtomValue(workspaceChallengeIdAtom)
  const task = useAtomValue(workspaceTaskAtom)
  const tableQueries = task?.tableQueries ?? []
  useAtomValue(workspaceTabsHasTwoViewsAtom)

  const [queryResults, setQueryResults] = useState<QueryResult | null>(null)
  const [isVisualizeDialogOpen, setIsVisualizeDialogOpen] = useState(false)
  const [subQuery, setSubQuery] = useState<string | null>(null)
  const [query, setQuery] = useState<string>(queryProp ?? '')
  const insertIntoEditorRef = useRef<((value: string) => void) | null>(null)
  const emitSolutionInsertHtml = useEventEmitter('solution:insertHtml')

  const onResultsChange = useCallback((result: QueryResult) => {
    setQueryResults(result)
  }, [])
  const { runQuery, isRunning } = useQueryRunner({
    challengeId: challengeId ?? '',
    onResultsChange,
  })

  const tables = useMemo(() => {
    return (
      tableQueries?.map((query) => {
        const { name } = getTableDataFromQuery(query)
        return name
      }) || []
    )
  }, [tableQueries])

  const tableColumns = useMemo(() => {
    return (
      tableQueries?.flatMap((query) => {
        const { fields } = getTableDataFromQuery(query)
        return fields.map((field) => field.name)
      }) || []
    )
  }, [tableQueries])

  const hasVisualizationData =
    !!queryResults &&
    !queryResults.error &&
    !!queryResults.results?.length

  const handleRunQuery = useCallback(
    async (queryOverride?: string, skipHistory = false) => {
      const queryToRun =
        queryOverride ?? subQuery ?? query ?? ''
      await runQuery(queryToRun, skipHistory)
    },
    [query, subQuery, runQuery]
  )

  const handleRunQueryRef = useRef(handleRunQuery)
  handleRunQueryRef.current = handleRunQuery

  useEffect(() => {
    setQuery(queryProp ?? '')
  }, [queryProp])

  useEffect(() => {
    if (challengeId) void handleRunQueryRef.current(undefined, true)
  }, [challengeId])

  const handleQueryChange = useCallback(
    (newQuery: string) => {
      setQuery(newQuery)
      onUpdateData?.(newQuery)
    },
    [onUpdateData]
  )

  if (!challengeId) return null

  return (
    <>
      <ResizablePanelGroup
        direction="vertical"
        className={cn('h-full min-h-0')}
      >
        <ResizablePanel
          defaultSize={50}
          minSize={20}
          maxSize={80}
        >
          <div className="h-full flex flex-col min-h-0">
            <SqlTabToolbar
              challengeId={challengeId}
              hasVisualizationData={hasVisualizationData}
              handlers={{
                run: () => void handleRunQuery(query),
                runPartial: () => void handleRunQuery(subQuery ?? undefined),
                history: noop,
                addToSolutionQuery: () => {
                  const q = subQuery ?? query
                  if (!q.trim()) return
                  const codeHtml = `<div class="my-2 p-1 border border-primary rounded-md" dir="ltr"><pre class="!m-0 !p-2 !bg-card text-sm font-mono whitespace-pre-wrap overflow-auto max-h-40"><code>${escapeHtml(q)}</code></pre></div><div>&nbsp;</div>`
                  emitSolutionInsertHtml({ value: codeHtml, deleteButton: true })
                  toast.success(t('common.queryAddedToSolution'))
                },
                addToSolutionVisualization: hasVisualizationData
                  ? () => {
                    void handleRunQuery(query)
                    setIsVisualizeDialogOpen(true)
                  }
                  : undefined,
              }}
              isRunning={isRunning}
              hasSelection={!!subQuery}
              onHistoryQuerySelect={(historyQuery) => {
                setQuery(historyQuery)
                void handleRunQuery(historyQuery, true)
              }}
            />
            <div className="flex-1 min-h-[140px] flex flex-col overflow-hidden">
              <Suspense
                fallback={
                  <div className="border border-border pt-2 bg-background h-[140px] flex items-center justify-center">
                    <StandardLoader size="md" />
                  </div>
                }
              >
                <CodeInput
                  query={query}
                  onQueryChange={handleQueryChange}
                  tableColumns={tableColumns}
                  tables={tables}
                  language="sql"
                  placeholder={task?.defaultQuery}
                  isLoading={isRunning}
                  onSelection={(selectedText) => setSubQuery(selectedText)}
                  onExecute={(editorQuery) => void handleRunQuery(editorQuery)}
                  registerInsertHandler={(insert) => {
                    insertIntoEditorRef.current = insert
                  }}
                />
              </Suspense>
            </div>
          </div>
        </ResizablePanel>

        <ResizableHandle />

        <ResizablePanel
          defaultSize={50}
          minSize={20}
          maxSize={80}
        >
          <div className="h-full flex flex-col min-h-0">
            <div className="flex items-center justify-between gap-2 p-2 shrink-0 border-border border-b">
              <span className="text-sm font-medium text-muted-foreground">
                {queryResults?.results?.length
                  ? t('challenges.resultsAMOUNT', {
                    amount: queryResults.results.length,
                  })
                  : t('common.results')}
              </span>
              <Badge variant="outline">
                {!queryResults?.isFromCache
                  ? `${queryResults?.timing ?? '...'}ms`
                  : t('common.cached')}
              </Badge>
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              <QueryResults
                queryResults={queryResults?.results}
                queryError={queryResults?.error || undefined}
                tableMode={false}
                isVisible={isVisible}
                isExecuting={isRunning}
              />
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>

      {hasVisualizationData && (
        <Dialog
          open={isVisualizeDialogOpen}
          onOpenChange={setIsVisualizeDialogOpen}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('common.visualizationChart')}</DialogTitle>
            </DialogHeader>
            <GenerateDataChart
              queryResults={queryResults?.results || []}
              query={query}
              onSubmit={() => setIsVisualizeDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      )}

    </>
  )
}
